/**
 * Evidence Lineage Visualization
 * Route: /lineage (or embedded in Audit module)
 *
 * Interactive force-directed graph showing:
 *   Source → Evidence → Datapoint → Framework Req → Claim → Report
 *
 * Each node type has distinct color + shape.
 * Click any node → detail panel slides in.
 * Demonstrates the complete audit trail visually.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { apiFetch } from '../services/api_service';


const C={bg:"#050a14",surface:"#0c1220",card:"#111c30",border:"#1a2744",text:"#e2e8f0",muted:"#6b7a96",dim:"#384660",blue:"#3b82f6",amber:"#f59e0b",emerald:"#10b981",purple:"#8b5cf6",cyan:"#06b6d4",red:"#ef4444",teal:"#14b8a6",orange:"#f97316",white:"#fff",indigo:"#6366f1",pink:"#ec4899"};
const Pl=({color:cl,children,small})=><span style={{display:"inline-flex",alignItems:"center",gap:3,padding:small?"1px 6px":"3px 9px",borderRadius:99,fontSize:small?8:10,fontWeight:600,background:cl+"12",color:cl,border:`1px solid ${cl}22`}}>{children}</span>;

const NODE_TYPES={
  source:{label:"Source",color:C.dim,icon:"📁",radius:12},
  evidence:{label:"Evidence",color:C.teal,icon:"📎",radius:16},
  datapoint:{label:"Datapoint",color:C.cyan,icon:"📊",radius:14},
  framework:{label:"Framework Req",color:C.purple,icon:"📜",radius:13},
  claim:{label:"Claim",color:C.emerald,icon:"✏️",radius:13},
  impact:{label:"Adverse Impact",color:C.red,icon:"⚠️",radius:15},
  supplier:{label:"Supplier",color:C.amber,icon:"🏭",radius:14},
};

// Demo lineage data (production: fetched from graph)
const DEMO_NODES=[
  {id:"src_bv",type:"source",label:"Bureau Veritas",detail:"Third-party audit firm"},
  {id:"src_hris",type:"source",label:"HRIS Export",detail:"TechAssembly payroll system"},
  {id:"ev_e1",type:"evidence",label:"Social Audit Report",detail:"48 pages · SHA:7a3f…c91d · Verified",hash:"sha256:7a3f…c91d",verified:true},
  {id:"ev_e2",type:"evidence",label:"Payroll Analysis",detail:"Overtime Hours Jul-Sep 2024",hash:"sha256:b2e4…f7a3",verified:true},
  {id:"ev_e15",type:"evidence",label:"CAP Progress Report",detail:"Month 4 of 6-month plan",hash:"sha256:c1d3…a9b2",verified:true},
  {id:"dp_s1_14",type:"datapoint",label:"TRIR: 1.8",detail:"Recordable incident rate · Approved"},
  {id:"dp_overtime",type:"datapoint",label:"Avg Hours: 62.3→53.8",detail:"Weekly hours · In Review"},
  {id:"fw_esrs_s114",type:"framework",label:"ESRS S1-14",detail:"Health & Safety Metrics"},
  {id:"fw_gri_4039",type:"framework",label:"GRI 403-9",detail:"Work-related injuries"},
  {id:"fw_esrs_s24",type:"framework",label:"ESRS S2-4",detail:"Value Chain Worker Impacts"},
  {id:"fw_csddd_a8",type:"framework",label:"CSDDD Art. 8",detail:"Identify Adverse Impacts"},
  {id:"cl_s114",type:"claim",label:"Claim: S1-14",detail:"TRIR = 1.8 per 200K hrs · Approved"},
  {id:"cl_s24",type:"claim",label:"Claim: S2-4",detail:"3 critical NCRs at TechAssembly · Draft"},
  {id:"imp_overtime",type:"impact",label:"Excessive Overtime",detail:"Sev:3 × Lik:5 = 15 · Actual"},
  {id:"sup_ta",type:"supplier",label:"TechAssembly Ltd",detail:"Vietnam · Tier 1 · Electronics"},
];

const DEMO_EDGES=[
  {from:"src_bv",to:"ev_e1"},{from:"src_hris",to:"ev_e2"},
  {from:"ev_e1",to:"dp_s1_14"},{from:"ev_e1",to:"dp_overtime"},
  {from:"ev_e2",to:"dp_overtime"},{from:"ev_e15",to:"dp_overtime"},
  {from:"dp_s1_14",to:"fw_esrs_s114"},{from:"dp_s1_14",to:"fw_gri_4039"},
  {from:"dp_overtime",to:"fw_esrs_s24"},{from:"dp_overtime",to:"fw_esrs_s114"},
  {from:"dp_overtime",to:"fw_csddd_a8"},
  {from:"fw_esrs_s114",to:"cl_s114"},{from:"fw_esrs_s24",to:"cl_s24"},
  {from:"sup_ta",to:"imp_overtime"},{from:"imp_overtime",to:"ev_e1"},
  {from:"imp_overtime",to:"ev_e2"},{from:"imp_overtime",to:"fw_esrs_s24"},
];

// Simple force simulation (no D3 dependency)
function useForceLayout(nodes,edges,width,height){
  const [positions,setPositions]=useState({});
  const frameRef=useRef(null);
  const velRef=useRef({});

  useEffect(()=>{
    // Initialize positions in columns by type
    const typeColumns={source:0.08,supplier:0.08,evidence:0.25,impact:0.25,datapoint:0.5,framework:0.72,claim:0.92};
    const typeCounts={};
    const initPos={};
    nodes.forEach(n=>{
      const col=typeColumns[n.type]||0.5;
      typeCounts[n.type]=(typeCounts[n.type]||0)+1;
      const row=typeCounts[n.type];
      const totalOfType=nodes.filter(x=>x.type===n.type).length;
      const ySpread=height*0.7;
      const yStart=(height-ySpread)/2;
      initPos[n.id]={x:col*width+(Math.random()-0.5)*30,y:yStart+(row/(totalOfType+1))*ySpread};
    });
    setPositions(initPos);
    velRef.current={};
    nodes.forEach(n=>{velRef.current[n.id]={vx:0,vy:0};});

    let frame=0;
    const maxFrames=200;
    const tick=()=>{
      if(frame>=maxFrames)return;
      frame++;
      const pos={...initPos};
      const vel=velRef.current;
      const alpha=1-frame/maxFrames;
      const repulse=800*alpha;
      const attract=0.005*alpha;
      const damping=0.85;

      // Repulsion between all nodes
      for(let i=0;i<nodes.length;i++){
        for(let j=i+1;j<nodes.length;j++){
          const a=nodes[i].id,b=nodes[j].id;
          const dx=(pos[b]?.x||0)-(pos[a]?.x||0);
          const dy=(pos[b]?.y||0)-(pos[a]?.y||0);
          const dist=Math.max(Math.sqrt(dx*dx+dy*dy),1);
          const f=repulse/(dist*dist);
          const fx=dx/dist*f,fy=dy/dist*f;
          vel[a].vx-=fx;vel[a].vy-=fy;
          vel[b].vx+=fx;vel[b].vy+=fy;
        }
      }

      // Attraction along edges
      edges.forEach(e=>{
        const dx=(pos[e.to]?.x||0)-(pos[e.from]?.x||0);
        const dy=(pos[e.to]?.y||0)-(pos[e.from]?.y||0);
        vel[e.from].vx+=dx*attract;vel[e.from].vy+=dy*attract;
        vel[e.to].vx-=dx*attract;vel[e.to].vy-=dy*attract;
      });

      // Apply velocity + damping + boundary
      nodes.forEach(n=>{
        vel[n.id].vx*=damping;vel[n.id].vy*=damping;
        pos[n.id]={
          x:Math.max(30,Math.min(width-30,(pos[n.id]?.x||0)+vel[n.id].vx)),
          y:Math.max(30,Math.min(height-30,(pos[n.id]?.y||0)+vel[n.id].vy)),
        };
      });

      Object.assign(initPos,pos);
      setPositions({...pos});
      frameRef.current=requestAnimationFrame(tick);
    };

    frameRef.current=requestAnimationFrame(tick);
    return()=>{if(frameRef.current)cancelAnimationFrame(frameRef.current);};
  },[nodes,edges,width,height]);

  return positions;
}

const API_BASE = typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL : "";

async function fetchLineageAPI(){
  try{
    // Fetch all evidence with their lineage chains
    const r=await fetch(`${API_BASE}/api/v1/ext/auditor-portal/evidence`,{credentials:"include"});
    if(!r.ok)return null;
    const text=await r.text();
    const data=JSON.parse(text);
    if(!data?.evidence?.length)return null;

    const nodes=[];const edges=[];const nodeIds=new Set();
    const addNode=(id,type,label,detail,extra)=>{if(!nodeIds.has(id)){nodeIds.add(id);nodes.push({id,type,label,detail,...(extra||{})});}};

    for(const ev of data.evidence){
      addNode(`ev_${ev.id}`, "evidence", ev.name?.slice(0,35)||ev.id, `${ev.type||""} · ${ev.hash?.slice(0,16)||""}`, {hash:ev.hash,verified:ev.verified});
      // Source node from uploadedBy
      if(ev.source||ev.uploadedBy){
        const srcId=`src_${(ev.source||ev.uploadedBy||"").replace(/[^a-zA-Z0-9]/g,"_").slice(0,20)}`;
        addNode(srcId,"source",(ev.source||ev.uploadedBy||"").slice(0,30),"Data source");
        edges.push({from:srcId,to:`ev_${ev.id}`});
      }
      // Datapoints
      for(const dpId of (ev.datapoint_ids||[])){
        addNode(`dp_${dpId}`,"datapoint",dpId,"Datapoint");
        edges.push({from:`ev_${ev.id}`,to:`dp_${dpId}`});
      }
      // Frameworks
      for(const fw of (ev.frameworks||[])){
        const fwId=`fw_${fw}`;
        addNode(fwId,"framework",fw,"Framework");
        edges.push({from:ev.datapoint_ids?.length?`dp_${ev.datapoint_ids[0]}`:`ev_${ev.id}`,to:fwId});
      }
      // Impacts
      for(const impId of (ev.impact_ids||[])){
        addNode(`imp_${impId}`,"impact",impId.slice(0,30),"Adverse Impact");
        edges.push({from:`imp_${impId}`,to:`ev_${ev.id}`});
      }
    }
    return nodes.length>3?{nodes,edges}:null;
  }catch{return null;}
}

export default function EvidenceLineage(){
  const [selected,setSelected]=useState(null);
  const [hoveredNode,setHoveredNode]=useState(null);
  const containerRef=useRef(null);
  const [dims,setDims]=useState({w:900,h:500});
  const [liveNodes,setLiveNodes]=useState(null);
  const [liveEdges,setLiveEdges]=useState(null);
  const [dataSource,setDataSource]=useState("loading");

  // Attempt live API fetch, fall back to demo
  useEffect(()=>{
    fetchLineageAPI().then(result=>{
      if(result){setLiveNodes(result.nodes);setLiveEdges(result.edges);setDataSource("live");}
      else{setDataSource("demo");}
    });
  },[]);

  useEffect(()=>{
    const el=containerRef.current;
    if(el){setDims({w:el.offsetWidth,h:Math.max(500,el.offsetHeight-40)});}
  },[]);

  const activeNodes=liveNodes||DEMO_NODES;
  const activeEdges=liveEdges||DEMO_EDGES;
  const positions=useForceLayout(activeNodes,activeEdges,dims.w,dims.h);
  const selectedNode=selected?activeNodes.find(n=>n.id===selected):null;
  const connectedEdges=selected?activeEdges.filter(e=>e.from===selected||e.to===selected):[];
  const connectedIds=new Set(connectedEdges.flatMap(e=>[e.from,e.to]));

  // Legend
  const legend=Object.entries(NODE_TYPES);

  return(<div ref={containerRef} style={{background:C.bg,minHeight:"100vh",color:C.text,fontFamily:"'Segoe UI',system-ui,sans-serif"}}>
    <div style={{padding:"10px 18px 6px",borderBottom:`1px solid ${C.border}`,position:"sticky",top:0,zIndex:10,background:C.bg+"f0",backdropFilter:"blur(12px)"}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <div style={{width:28,height:28,borderRadius:6,background:`linear-gradient(135deg,${C.indigo},${C.purple})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:C.white}}>📐</div>
        <div><div style={{fontSize:14,fontWeight:800}}>Evidence Lineage</div><div style={{fontSize:8,color:C.muted}}>Source → Evidence → Datapoint → Framework → Claim</div></div>
        <div style={{flex:1}}/>
        <Pl color={dataSource==="live"?C.emerald:C.amber} small>{dataSource==="live"?`✓ Live (${activeNodes.length} nodes)`:"Demo Data"}</Pl>
        {dataSource==="demo"&&<button onClick={()=>{fetchLineageAPI().then(r=>{if(r){setLiveNodes(r.nodes);setLiveEdges(r.edges);setDataSource("live");}});}} style={{padding:"3px 8px",borderRadius:4,border:`1px solid ${C.cyan}33`,background:"transparent",color:C.cyan,fontSize:8,cursor:"pointer"}}>↻ Retry API</button>}
        <div style={{display:"flex",gap:4}}>{legend.map(([k,v])=><span key={k} style={{display:"flex",alignItems:"center",gap:3,fontSize:7,color:v.color}}><span style={{width:8,height:8,borderRadius:"50%",background:v.color}}/>{v.label}</span>)}</div>
      </div>
    </div>

    <div style={{display:"grid",gridTemplateColumns:selected?"1fr 300px":"1fr"}}>
      {/* SVG Graph */}
      <div style={{padding:"8px"}}>
        <svg width={dims.w-20} height={dims.h} style={{background:C.surface,borderRadius:8,border:`1px solid ${C.border}`}}>
          <defs>
            <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><polygon points="0 0, 8 3, 0 6" fill={C.dim+"88"}/></marker>
            <filter id="glow"><feGaussianBlur stdDeviation="3" result="g"/><feMerge><feMergeNode in="g"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
          </defs>

          {/* Edges */}
          {activeEdges.map((e,i)=>{
            const from=positions[e.from],to=positions[e.to];
            if(!from||!to)return null;
            const isConnected=selected&&(e.from===selected||e.to===selected);
            const isActive=!selected||isConnected;
            return<line key={i} x1={from.x} y1={from.y} x2={to.x} y2={to.y}
              stroke={isConnected?C.cyan+"88":C.dim+"33"}
              strokeWidth={isConnected?2:1}
              strokeDasharray={isConnected?undefined:"4 3"}
              opacity={isActive?1:0.15}
              markerEnd={isConnected?"url(#arrowhead)":""}
              style={{transition:"all .3s"}}/>;
          })}

          {/* Nodes */}
          {activeNodes.map(n=>{
            const pos=positions[n.id];
            if(!pos)return null;
            const nt=NODE_TYPES[n.type]||NODE_TYPES.source;
            const isSel=selected===n.id;
            const isConn=selected&&connectedIds.has(n.id);
            const isHov=hoveredNode===n.id;
            const isActive=!selected||isSel||isConn;
            return<g key={n.id} onClick={()=>setSelected(isSel?null:n.id)}
              onMouseEnter={()=>setHoveredNode(n.id)} onMouseLeave={()=>setHoveredNode(null)}
              style={{cursor:"pointer",transition:"opacity .3s",opacity:isActive?1:0.12}}>
              <circle cx={pos.x} cy={pos.y} r={nt.radius+(isSel?4:isHov?2:0)}
                fill={isSel?nt.color+"33":nt.color+"18"}
                stroke={nt.color} strokeWidth={isSel?2.5:isHov?2:1.5}
                filter={isSel?"url(#glow)":undefined}/>
              <text x={pos.x} y={pos.y+1} textAnchor="middle" dominantBaseline="middle"
                fontSize={nt.radius>14?12:10} style={{pointerEvents:"none"}}>{nt.icon}</text>
              <text x={pos.x} y={pos.y+nt.radius+10} textAnchor="middle"
                fill={isActive?C.text:C.dim} fontSize={8} fontWeight={isSel?700:400}
                style={{pointerEvents:"none"}}>{n.label}</text>
            </g>;
          })}
        </svg>
      </div>

      {/* Detail Panel */}
      {selected&&selectedNode&&<div style={{padding:"12px",borderLeft:`1px solid ${C.border}`,overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
          <span style={{fontSize:20}}>{NODE_TYPES[selectedNode.type]?.icon}</span>
          <div><div style={{fontSize:12,fontWeight:800,color:NODE_TYPES[selectedNode.type]?.color}}>{selectedNode.label}</div>
            <Pl color={NODE_TYPES[selectedNode.type]?.color} small>{NODE_TYPES[selectedNode.type]?.label}</Pl></div>
          <button onClick={()=>setSelected(null)} style={{marginLeft:"auto",padding:"2px 6px",borderRadius:3,border:`1px solid ${C.border}`,background:"transparent",color:C.muted,fontSize:8,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:9,color:C.muted,marginBottom:8}}>{selectedNode.detail}</div>

        {selectedNode.hash&&<div style={{padding:"4px 6px",borderRadius:3,background:C.card,fontSize:7,fontFamily:"monospace",color:C.teal,marginBottom:4}}>Hash: {selectedNode.hash}</div>}
        {selectedNode.verified!==undefined&&<Pl color={selectedNode.verified?C.emerald:C.red} small>{selectedNode.verified?"✓ Verified":"⚠ Unverified"}</Pl>}

        <div style={{marginTop:10}}>
          <div style={{fontSize:8,fontWeight:700,color:C.cyan,marginBottom:4}}>CONNECTIONS ({connectedEdges.length})</div>
          {connectedEdges.map((e,i)=>{
            const otherId=e.from===selected?e.to:e.from;
            const other=activeNodes.find(n=>n.id===otherId);
            const direction=e.from===selected?"→":"←";
            return<div key={i} onClick={()=>setSelected(otherId)} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 6px",borderRadius:3,background:C.card,marginBottom:2,cursor:"pointer",fontSize:8}}>
              <span style={{color:C.cyan}}>{direction}</span>
              <span style={{fontSize:10}}>{NODE_TYPES[other?.type]?.icon}</span>
              <span style={{flex:1}}>{other?.label}</span>
              <Pl color={NODE_TYPES[other?.type]?.color} small>{NODE_TYPES[other?.type]?.label}</Pl>
            </div>;
          })}
        </div>
      </div>}
    </div>
  </div>);
}
